<template lang="pug">
.notification-list
  //- サイドメニュー部
  side-menu.notification-list__side-menu(
    :userRole='userRole',
    :currentLink='currentLink',
    :linkEvent='sideMenuLinkEvent',
    :logoutEvent='logoutEvent'
  )
    template(#separateText)
      slot(name='sideMenuSeparateText')
    template(#logoutText)
      slot(name='logoutText')
  //- コンテンツ表示部
  .notification-list__content
    breadcrumb-list.notification-list__content__breadcrumb(
      :breadcrumbs='breadcrumbs',
      :buttonEvent='openRegisterModal'
    )
      template(#buttonLabel)
        slot(name='breadcrumbButtonLabel')
    .notification-list__content__body
      .notification-list__content__body__main
        .notification-list__content__body__main__action
          search-notification.notification-list__content__body__main__action__search(
            :titleName='titleName',
            :titleNamePlaceholder='titleNamePlaceholder',
            :titleNameChangeEvent='setTitleName',
            :searchEvent='searchEvent'
          )
            template(#searchButtonText)
              slot(name='searchButtonText')
        notification-list-table.notification-list__content__body__main__table(
          v-if='sort && notificationDatas',
          :sort='sort',
          :sortChangeEvent='changeSort',
          :notificationDatas='notificationDatas',
          :deleteEvent='deleteEvent',
          :editEvent='openEditModal'
        )
          template(#titleHeader)
            slot(name='titleHeader')
          template(#startDateHeader)
            slot(name='startDateHeader')
          template(#endDateHeader)
            slot(name='endDateHeader')
          template(#editHeader)
            slot(name='editHeader')
          template(#popupRemoveText)
            slot(name='popupRemoveText')
          template(#popupEditText)
            slot(name='popupEditText')
        .notification-list__content__body__main__footer
          button-pagination.notification-list__content__body__main__footer__pager(
            :status='pagenationStatus',
            @click='pagenationEvent'
          )
  //- モーダル部
  modal-notification-edit.notification-list__modal(
    v-if='notificationEditData',
    ref='modalNotificationEdit',
    :title='modalTitle',
    :notificationEditData='notificationEditData',
    :titlePlaceholder='modalTitlePlaceholder',
    :startDatePlaceholder='modalStartDatePlaceholder',
    :endDatePlaceholder='modalEndDatePlaceholder',
    :messagePlaceholder='modalMessagePlaceholder',
    :keepButtonEvent='modalKeepButtonEvent',
    :errorMessages='modalErrorMessages'
  )
    template(#titleLabel)
      slot(name='modalTitleLabel')
    template(#startDateLabel)
      slot(name='modalStartDateLabel')
    template(#endDateLabel)
      slot(name='modalEndDateLabel')
    template(#messageLabel)
      slot(name='modalMessageLabel')
    template(#keepText)
      slot(name='modalKeepText')
</template>

<script lang="ts">
import { Component, Prop, Vue, Ref } from 'vue-property-decorator'
import SideMenu from '@/components/organisms/manager/SideMenu.vue'
import BreadcrumbList from '@/components/organisms/manager/BreadcrumbList.vue'
import SearchNotification from '@/components/organisms/manager/SearchNotification.vue'
import NotificationListTable from '@/components/organisms/manager/NotificationListTable.vue'
import ButtonPagination from '@/components/molecules/manager/ButtonPagination.vue'
import ModalNotificationEdit from '@/components/organisms/manager/ModalNotificationEdit.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import { NotificationListType, NotificationListSortType, NotificationEditType } from '@/models/notificationModel'
import { PaginationStatus } from '@/models/paginationModel'
import { LinkSetting } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { SortSetting } from '@/const/SortSettings'

/**
 * お知らせ管理画面
 */
@Component({
  components: {
    SideMenu,
    BreadcrumbList,
    SearchNotification,
    NotificationListTable,
    ButtonPagination,
    ModalNotificationEdit,
  },
})
export default class NotificationList extends Vue {
  // ユーザ権限
  @Prop()
  userRole!: UserRoles

  // このページのリンク設定
  @Prop()
  currentLink!: LinkSetting

  // サイドメニューリンク押下時のイベント
  @Prop()
  sideMenuLinkEvent!: Function

  // ログアウトボタン押下時のイベント
  @Prop()
  logoutEvent!: Function

  // パンくず一覧情報
  @Prop()
  breadcrumbs!: breadcrumbType[]

  // 新規登録用の情報設定イベント
  @Prop()
  setRegisterDataEvent!: Function

  // 件名プレースホルダー
  @Prop()
  titleNamePlaceholder!: Function

  // 検索ボタン押下時イベント
  @Prop()
  searchEvent!: Function

  // ソート情報
  @Prop()
  sort!: NotificationListSortType

  // お知らせ一覧情報
  @Prop()
  notificationDatas!: NotificationListType[]

  // 削除ボタン押下時イベント
  @Prop()
  deleteEvent!: Function

  // 編集用の情報設定イベント
  @Prop()
  setEditDataEvent!: Function

  // ページネーションの状態
  @Prop()
  pagenationStatus!: PaginationStatus

  // ページネーションイベント
  @Prop()
  pagenationEvent!: Function

  // モーダルのタイトル
  @Prop()
  modalTitle!: string

  // 編集対象のお知らせ情報
  @Prop()
  notificationEditData!: NotificationEditType

  // モーダルの件名プレースホルダー
  @Prop()
  modalTitlePlaceholder!: string

  // モーダルの表示開始日時プレースホルダー
  @Prop()
  modalStartDatePlaceholder!: string

  // モーダルの表示終了日時プレースホルダー
  @Prop()
  modalEndDatePlaceholder!: string

  // モーダルの本文プレースホルダー
  @Prop()
  modalMessagePlaceholder!: string

  // モーダルの保存ボタン押下時イベント
  @Prop()
  modalKeepButtonEvent!: Function

  // モーダルに表示するエラーメッセージ
  @Prop()
  modalErrorMessages!: string[]

  // モーダルref
  @Ref()
  modalNotificationEdit!: ModalNotificationEdit

  // === 選択値を保持する変数群
  // 件名
  public titleName = ''

  // === 選択値をセットする関数群
  // 件名をセット
  private setTitleName(value: string) {
    this.titleName = value
  }

  // ソート値を変更
  private changeSort(sortSetting: SortSetting, sortTarget: string) {
    const sort = this.sort
    sort[sortTarget] = sortSetting
    this.$emit('sort-changed', sort)
  }

  // 新規登録モーダルを開く
  private async openRegisterModal() {
    await this.setRegisterDataEvent()
    if (this.notificationEditData) {
      this.modalNotificationEdit.show()
    }
  }

  // 編集モーダルを開く
  private async openEditModal(id: number) {
    await this.setEditDataEvent(id)
    if (this.notificationEditData) {
      this.modalNotificationEdit.show()
    }
  }
}
</script>

<style lang="stylus" scoped>
.notification-list
  display flex
  width 100%
  height 100%

  &__content
    width 100%
    height 100%
    display flex
    flex-direction column
    padding 40px
    gap 20px
    overflow-y auto

    &__body
      display flex
      flex-direction column

      &__main
        width 100%
        min-height 500px
        display flex
        flex-direction column
        border-radius 15px
        background var(--color-manager-list__background)
        box-shadow 0px 3px 5px 0px var(--color-manager-list__shadow)

        &__action
          display flex
          justify-content space-between
          align-items center
          padding 12px 20px

          &__search
            padding 7px 5px

        &__footer
          display flex
          justify-content flex-end
          align-items center
          padding 20px 30px
          margin-top auto
</style>
